import { NULL_PRICE, PriceKey, renecPriceDataAtom } from "@/atoms/priceState"
import { restakingAccountAtom, restakingVaultState } from "@/atoms/restakingState"
import { connectionAtom } from "@/atoms/settings"
import { allTokenInfosAtom } from "@/atoms/tokenInfos"
import { calculateTimeRemaining } from "@/components/staking/components/detail/components/shared/commons"
import { IDL } from "@/programs/restaking_program"
import {
  DAYS_IN_YEAR,
  DEFAULT_DECIMALS,
  RESTAKING_ADMIN_ADDRESS,
  RESTAKING_MINT,
  RESTAKING_PROGRAM_ID,
  RESTAKING_REWARD_MINT,
  SECONDS_IN_YEAR,
} from "@/utils/constants"
import { commonErrorResponse, getOrCreateAssociatedTokenAccountInstruction } from "@/utils/helpers"
import * as anchor from "@project-serum/anchor"
import { AnchorProvider } from "@project-serum/anchor"
import NodeWallet from "@project-serum/anchor/dist/cjs/nodewallet" // using this because we are on browser
import { DemonWalletAdapter } from "@renec-foundation/wallet-adapter-react"
import { ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID, Token } from "@solana/spl-token"
import { Keypair, PublicKey, SystemProgram, Transaction } from "@solana/web3.js"
import BN from "bn.js"
import { useAtomValue } from "jotai"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import useCheckingOnChain from "./useCheckingOnChain"
import useTrans from "./useTrans"
import useWalletConnect from "./useWalletConnect"

export type RestakingResult = {
  isSucceeded: boolean
  tx?: string
  error?: string
}

export type RestakingAccountResult = {
  isSucceeded: boolean
  result?: any
  error?: string
}

const opts: anchor.web3.ConfirmOptions = {
  preflightCommitment: "finalized",
  commitment: "finalized",
}

const CONFIG_SEED = "mywhisk3yneat"
const VAULT_SEED = "mycoff33black"
const FEE_SEED = "an3mptyshare"
const STAKING_ACCOUNT_SEED = "assoftasra1n"
const MINT = new PublicKey(RESTAKING_MINT)
const REWARD_MINT = new PublicKey(RESTAKING_REWARD_MINT)
const ADMIN_ACCOUNT = new PublicKey(RESTAKING_ADMIN_ADDRESS)
const PROGRAM_ID = new PublicKey(RESTAKING_PROGRAM_ID)

const RANDOM_KEYPAIR = Keypair.generate()
const RANDOM_WALLET = new NodeWallet(RANDOM_KEYPAIR)

const useRestaking = () => {
  const trans = useTrans()
  const { wallet, address } = useWalletConnect()
  const connection = useAtomValue(connectionAtom)
  const { getSPLTokenBalance } = useCheckingOnChain()
  const provider = new AnchorProvider(connection, RANDOM_WALLET, opts)
  const program = new anchor.Program(IDL, PROGRAM_ID, provider)
  const renecPriceData = useAtomValue(renecPriceDataAtom)
  const restakingVault = useAtomValue(restakingVaultState)
  const restakingAccount = useAtomValue(restakingAccountAtom)
  const allTokenInfos = useAtomValue(allTokenInfosAtom)
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000))

  // used to cancel the previous interval when we call this hook many times.
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const findProgramAddress = useCallback((seeds: (string | Uint8Array)[], programId: PublicKey) => {
    return anchor.web3.PublicKey.findProgramAddressSync(
      seeds.map((seed) => (typeof seed === "string" ? Buffer.from(seed) : seed)),
      programId,
    )
  }, [])

  const initializeStakingAccountFn = async () => {
    if (!address) {
      throw new Error("Wallet not found")
    }
    const walletPubkey = new PublicKey(address)
    const [configAccount] = findProgramAddress([CONFIG_SEED, ADMIN_ACCOUNT.toBuffer()], PROGRAM_ID)
    const [vaultAccount] = findProgramAddress([VAULT_SEED, configAccount.toBuffer()], PROGRAM_ID)
    const [stakingAccount] = findProgramAddress(
      [STAKING_ACCOUNT_SEED, vaultAccount.toBuffer(), walletPubkey.toBuffer()],
      PROGRAM_ID,
    )
    const [feeAccount] = findProgramAddress([FEE_SEED, configAccount.toBuffer()], PROGRAM_ID)

    const instructions = [
      await program.methods
        .initializeAccount()
        .accounts({
          stakingAccount,
          owner: walletPubkey,
          vault: vaultAccount,
          configAccount,
          feeAccount,
          systemProgram: SystemProgram.programId,
          rent: anchor.web3.SYSVAR_RENT_PUBKEY,
        })
        .instruction(),
    ]

    return instructions
  }

  const stakingFn = async (amount: number, decimals: number): Promise<RestakingResult> => {
    try {
      if (address && wallet) {
        const walletPubkey = new PublicKey(address)
        const [configAccount] = findProgramAddress([CONFIG_SEED, ADMIN_ACCOUNT.toBuffer()], PROGRAM_ID)
        const [vaultAccount] = findProgramAddress([VAULT_SEED, configAccount.toBuffer()], PROGRAM_ID)
        const [stakingAccount] = findProgramAddress(
          [STAKING_ACCOUNT_SEED, vaultAccount.toBuffer(), walletPubkey.toBuffer()],
          PROGRAM_ID,
        )
        const [feeAccount] = findProgramAddress([FEE_SEED, configAccount.toBuffer()], PROGRAM_ID)

        const { ixs: createAtaIxs, userAtaAddress } = await getOrCreateAssociatedTokenAccountInstruction(
          connection,
          walletPubkey,
          walletPubkey,
          MINT,
        )

        const vaultAta = await Token.getAssociatedTokenAddress(
          ASSOCIATED_TOKEN_PROGRAM_ID,
          TOKEN_PROGRAM_ID,
          MINT,
          vaultAccount,
          true,
        )

        // Check case when user didn't have staking account, then init it firstly.
        const initStakingAccountIxs = []
        const stakingAccountResult = await fetchStakingAccountFn()
        if (!stakingAccountResult.isSucceeded) {
          const ixs = await initializeStakingAccountFn()
          initStakingAccountIxs.push(...ixs)
        }

        // Check case when user has unstaked amount then stop unstake 1st, then stake.
        const stopUnstakingIxs = []
        if (restakingAccount && restakingAccount?.unstakedAmount.toNumber() > 0) {
          const ix = await program.methods
            .stopUnstaking()
            .accounts({
              owner: walletPubkey,
              stakingAccount,
              vault: vaultAccount,
              configAccount,
              feeAccount,
              systemProgram: SystemProgram.programId,
            })
            .instruction()
          stopUnstakingIxs.push(ix)
        }

        const stakingIxs = [
          await program.methods
            .stake(new BN(Math.floor(amount * 10 ** decimals)))
            .accounts({
              owner: walletPubkey,
              stakingAccount,
              userTokenAccount: userAtaAddress,
              vaultTokenAccount: vaultAta,
              vault: vaultAccount,
              tokenMint: MINT,
              tokenProgram: TOKEN_PROGRAM_ID,
              configAccount,
              feeAccount,
              systemProgram: SystemProgram.programId,
            })
            .instruction(),
        ]

        const transaction = new Transaction()
        if (createAtaIxs.length > 0) {
          transaction.add(...createAtaIxs)
        }
        if (initStakingAccountIxs.length > 0) {
          transaction.add(...initStakingAccountIxs)
        }
        if (stopUnstakingIxs.length > 0) {
          transaction.add(...stopUnstakingIxs)
        }
        transaction.add(...stakingIxs)
        transaction.recentBlockhash = (await connection.getLatestBlockhash("finalized")).blockhash
        transaction.feePayer = walletPubkey

        let txSignature: string
        if (wallet.adapter instanceof DemonWalletAdapter) {
          txSignature = await (wallet.adapter as DemonWalletAdapter).sendTransactionWithSigners(
            transaction,
            connection,
            [],
          )
        } else {
          txSignature = await wallet.adapter.sendTransaction(transaction, connection)
        }

        return {
          isSucceeded: true,
          tx: txSignature,
        }
      }

      throw new Error("Wallet not found")
    } catch (err: any) {
      return commonErrorResponse(err, trans)
    }
  }

  const unstakingNWithdrawFn = async (amount: number, decimals: number): Promise<RestakingResult> => {
    try {
      if (address && wallet) {
        const walletPubkey = new PublicKey(address)

        const [configAccount] = findProgramAddress([CONFIG_SEED, ADMIN_ACCOUNT.toBuffer()], PROGRAM_ID)
        const [vaultAccount] = findProgramAddress([VAULT_SEED, configAccount.toBuffer()], PROGRAM_ID)
        const [stakingAccount] = findProgramAddress(
          [STAKING_ACCOUNT_SEED, vaultAccount.toBuffer(), walletPubkey.toBuffer()],
          PROGRAM_ID,
        )
        const [feeAccount] = findProgramAddress([FEE_SEED, configAccount.toBuffer()], PROGRAM_ID)

        const { ixs: createAtaIxs, userAtaAddress } = await getOrCreateAssociatedTokenAccountInstruction(
          connection,
          walletPubkey,
          walletPubkey,
          MINT,
        )

        const vaultAta = await Token.getAssociatedTokenAddress(
          ASSOCIATED_TOKEN_PROGRAM_ID,
          TOKEN_PROGRAM_ID,
          MINT,
          vaultAccount,
          true,
        )

        const instructions = [
          await program.methods
            .unstakeNWithdraw(new BN(Math.floor(amount * 10 ** decimals)))
            .accounts({
              owner: walletPubkey,
              stakingAccount,
              userTokenAccount: userAtaAddress,
              vaultTokenAccount: vaultAta,
              vault: vaultAccount,
              tokenMint: MINT,
              tokenProgram: TOKEN_PROGRAM_ID,
              configAccount,
              feeAccount,
              systemProgram: SystemProgram.programId,
            })
            .instruction(),
        ]

        const transaction = new Transaction()
        if (createAtaIxs.length > 0) {
          transaction.add(...createAtaIxs)
        }
        transaction.add(...instructions)
        transaction.recentBlockhash = (await connection.getLatestBlockhash("finalized")).blockhash
        transaction.feePayer = walletPubkey

        let txSignature: string
        if (wallet.adapter instanceof DemonWalletAdapter) {
          txSignature = await (wallet.adapter as DemonWalletAdapter).sendTransactionWithSigners(
            transaction,
            connection,
            [],
          )
        } else {
          txSignature = await wallet.adapter.sendTransaction(transaction, connection)
        }

        return {
          isSucceeded: true,
          tx: txSignature,
        }
      }
      throw new Error("Wallet not found")
    } catch (err: any) {
      return commonErrorResponse(err, trans)
    }
  }

  const stopUnstakingFn = async (): Promise<RestakingResult> => {
    try {
      if (address && wallet) {
        const walletPubkey = new PublicKey(address)

        const [configAccount] = findProgramAddress([CONFIG_SEED, ADMIN_ACCOUNT.toBuffer()], PROGRAM_ID)
        const [vaultAccount] = findProgramAddress([VAULT_SEED, configAccount.toBuffer()], PROGRAM_ID)
        const [stakingAccount] = findProgramAddress(
          [STAKING_ACCOUNT_SEED, vaultAccount.toBuffer(), walletPubkey.toBuffer()],
          PROGRAM_ID,
        )
        const [feeAccount] = findProgramAddress([FEE_SEED, configAccount.toBuffer()], PROGRAM_ID)

        const instructions = [
          await program.methods
            .stopUnstaking()
            .accounts({
              owner: walletPubkey,
              stakingAccount,
              vault: vaultAccount,
              configAccount,
              feeAccount,
              systemProgram: SystemProgram.programId,
            })
            .instruction(),
        ]

        const transaction = new Transaction()
        transaction.add(...instructions)
        transaction.recentBlockhash = (await connection.getLatestBlockhash("finalized")).blockhash
        transaction.feePayer = walletPubkey

        let txSignature: string
        if (wallet.adapter instanceof DemonWalletAdapter) {
          txSignature = await (wallet.adapter as DemonWalletAdapter).sendTransactionWithSigners(
            transaction,
            connection,
            [],
          )
        } else {
          txSignature = await wallet.adapter.sendTransaction(transaction, connection)
        }

        return {
          isSucceeded: true,
          tx: txSignature,
        }
      }
      throw new Error("Wallet not found")
    } catch (err: any) {
      return commonErrorResponse(err, trans)
    }
  }

  const claimRewardFn = async (): Promise<RestakingResult> => {
    try {
      if (address && wallet) {
        const walletPubkey = new PublicKey(address)

        const [configAccount] = findProgramAddress([CONFIG_SEED, ADMIN_ACCOUNT.toBuffer()], PROGRAM_ID)
        const [vaultAccount] = findProgramAddress([VAULT_SEED, configAccount.toBuffer()], PROGRAM_ID)
        const [stakingAccount] = findProgramAddress(
          [STAKING_ACCOUNT_SEED, vaultAccount.toBuffer(), walletPubkey.toBuffer()],
          PROGRAM_ID,
        )
        const [feeAccount] = findProgramAddress([FEE_SEED, configAccount.toBuffer()], PROGRAM_ID)

        const { ixs: createAtaIxs, userAtaAddress } = await getOrCreateAssociatedTokenAccountInstruction(
          connection,
          walletPubkey,
          walletPubkey,
          REWARD_MINT,
        )

        const vaultAta = await Token.getAssociatedTokenAddress(
          ASSOCIATED_TOKEN_PROGRAM_ID,
          TOKEN_PROGRAM_ID,
          REWARD_MINT,
          vaultAccount,
          true,
        )
        const instructions = [
          await program.methods
            .claimReward()
            .accounts({
              owner: walletPubkey,
              stakingAccount,
              userTokenAccount: userAtaAddress,
              vaultTokenAccount: vaultAta,
              vault: vaultAccount,
              rewardTokenMint: REWARD_MINT,
              tokenProgram: TOKEN_PROGRAM_ID,
              configAccount,
              feeAccount,
              systemProgram: SystemProgram.programId,
            })
            .instruction(),
        ]

        const transaction = new Transaction()
        if (createAtaIxs.length > 0) {
          transaction.add(...createAtaIxs)
        }
        transaction.add(...instructions)
        transaction.recentBlockhash = (await connection.getLatestBlockhash("finalized")).blockhash
        transaction.feePayer = walletPubkey

        let txSignature: string
        if (wallet.adapter instanceof DemonWalletAdapter) {
          txSignature = await (wallet.adapter as DemonWalletAdapter).sendTransactionWithSigners(
            transaction,
            connection,
            [],
          )
        } else {
          txSignature = await wallet.adapter.sendTransaction(transaction, connection)
        }

        return {
          isSucceeded: true,
          tx: txSignature,
        }
      }
      throw new Error("Wallet not found")
    } catch (err: any) {
      return commonErrorResponse(err, trans)
    }
  }

  const fetchStakingAccountFn = useCallback(async (): Promise<RestakingAccountResult> => {
    try {
      if (!address) {
        throw new Error("Wallet not found")
      }
      const walletPubkey = new PublicKey(address)

      const [configAccount] = findProgramAddress([CONFIG_SEED, ADMIN_ACCOUNT.toBuffer()], PROGRAM_ID)
      const [vaultAccount] = findProgramAddress([VAULT_SEED, configAccount.toBuffer()], PROGRAM_ID)
      const [stakingAccount] = findProgramAddress(
        [STAKING_ACCOUNT_SEED, vaultAccount.toBuffer(), walletPubkey.toBuffer()],
        PROGRAM_ID,
      )
      const account = await program.account.stakingAccount.fetch(stakingAccount)

      return {
        isSucceeded: true,
        result: account,
      }
    } catch (err: any) {
      return commonErrorResponse(err, trans)
    }
  }, [findProgramAddress, program.account.stakingAccount, trans, address])

  const fetchVaultFn = async () => {
    try {
      const [configAccount] = findProgramAddress([CONFIG_SEED, ADMIN_ACCOUNT.toBuffer()], PROGRAM_ID)
      const [vaultAccount] = findProgramAddress([VAULT_SEED, configAccount.toBuffer()], PROGRAM_ID)

      const vault = await program.account.vault.fetch(vaultAccount)

      return {
        isSucceeded: true,
        result: vault,
      }
    } catch (err: any) {
      return commonErrorResponse(err, trans)
    }
  }

  const fetchAssetsFn = useCallback(
    async (mint: PublicKey) => {
      try {
        if (!address) {
          throw new Error("Wallet not found")
        }

        const walletPubkey = new PublicKey(address)

        const userAta = await Token.getAssociatedTokenAddress(
          ASSOCIATED_TOKEN_PROGRAM_ID,
          TOKEN_PROGRAM_ID,
          mint,
          walletPubkey,
          true,
        )
        const balance = await getSPLTokenBalance(userAta)
        return {
          isSucceeded: true,
          result: balance,
        }
      } catch (err: any) {
        console.error(err)
        return {}
      }
    },
    [getSPLTokenBalance, address],
  )

  const fetchAvailableReward = useCallback(async () => {
    return fetchAssetsFn(REWARD_MINT)
  }, [fetchAssetsFn])

  const fetchAvailableREL = useCallback(async () => {
    return fetchAssetsFn(MINT)
  }, [fetchAssetsFn])

  /**
   * Addtionals calculation for common UI
   */
  // infos
  const stakingItemInfo = useMemo(() => allTokenInfos.find((info) => info.address === RESTAKING_MINT), [allTokenInfos])
  const rewardItemInfo = useMemo(
    () => allTokenInfos.find((info) => info.address === restakingVault?.rewards[0].rewardTokenMint.toBase58()),
    [allTokenInfos, restakingVault],
  )

  // price
  const stakedTokenPrice = useMemo(
    () => (restakingAccount ? renecPriceData[stakingItemInfo?.symbol as PriceKey] ?? NULL_PRICE : NULL_PRICE),
    [renecPriceData, stakingItemInfo, restakingAccount],
  )
  const rewardTokenPrice = useMemo(
    () => renecPriceData[rewardItemInfo?.symbol as PriceKey] ?? NULL_PRICE,
    [renecPriceData, rewardItemInfo],
  )

  // calculations
  const unlockTimestamp = restakingAccount?.unlockTimestamp.toNumber() ?? 0
  const timeRemainingTillUnlock = calculateTimeRemaining(unlockTimestamp)
  const stakingTokenDecimals = stakingItemInfo?.decimals ?? DEFAULT_DECIMALS
  const rewardTokenDecimals = rewardItemInfo?.decimals ?? DEFAULT_DECIMALS
  const stakedAmountUI = restakingAccount ? restakingAccount.stakedAmount.toNumber() / 10 ** stakingTokenDecimals : 0
  const unstakedAmountUI = restakingAccount
    ? restakingAccount.unstakedAmount.toNumber() / 10 ** stakingTokenDecimals
    : 0

  /**
   * Calculate APY
   */
  const userYearlyReward = useMemo(() => {
    if (!restakingVault) return 0
    return (restakingVault.vaultStat.emissionPerDay * DAYS_IN_YEAR) / restakingVault.vaultStat.totalStakers
  }, [restakingVault])
  const currentTotalStakedAmount = useMemo(() => {
    if (!restakingVault) return 0
    return restakingVault.vaultStat.totalStakedAmount / 10 ** stakingTokenDecimals
  }, [restakingVault, stakingTokenDecimals])
  const currentStakingAPY = useMemo(() => {
    if (!restakingVault || !currentTotalStakedAmount) return 0
    return userYearlyReward / currentTotalStakedAmount
  }, [currentTotalStakedAmount, restakingVault, userYearlyReward])

  /**
   * Calculate total claimable reward
   */
  const accumulatedRewardAmount = useMemo(() => {
    if (!restakingAccount) return 0
    return restakingAccount.rewards[0].accumulatedRewardAmount.toNumber() / 10 ** rewardTokenDecimals
  }, [restakingAccount, rewardTokenDecimals])
  const totalRewardPerSecond = useMemo(() => {
    if (!restakingAccount || !restakingVault) return 0
    const rewardPerSecond = (currentStakingAPY / SECONDS_IN_YEAR) * stakedAmountUI
    return rewardPerSecond
  }, [currentStakingAPY, restakingAccount, restakingVault, stakedAmountUI])
  const timeElapsedFromLastStaked =
    currentTime - (restakingAccount?.lastStaked.toNumber() ?? Math.floor(Date.now() / 1000))
  const totalClaimable = accumulatedRewardAmount + totalRewardPerSecond * timeElapsedFromLastStaked

  // Countdown cooldown timer
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    intervalRef.current = setInterval(() => {
      setCurrentTime(Math.floor(Date.now() / 1000))
    }, 1000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  return {
    address,
    stakingFn,
    unstakingNWithdrawFn,
    stopUnstakingFn,
    claimRewardFn,
    fetchAvailableREL,
    fetchAvailableReward,
    fetchStakingAccountFn,
    fetchVaultFn,
    stakingItemInfo,
    rewardItemInfo,
    stakedTokenPrice,
    rewardTokenPrice,
    stakedAmountUI,
    unstakedAmountUI,
    timeRemainingTillUnlock,
    totalClaimable,
    currentTotalStakedAmount,
    currentStakingAPY,
  }
}

export default useRestaking
